import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import CategoryScoresTable from './CategoryScoresTable';
import './FindingsRecommendations.css';

const FindingsRecommendations = ({ assessment, findings }) => {
  console.log('FindingsRecommendations component rendered with:', { assessment, findings });
  
  useEffect(() => {
    console.log('Assessment data:', assessment);
    console.log('Findings data:', findings);
    if (findings && findings.categoryFindings) {
      console.log('Category findings:', findings.categoryFindings);
      console.log('Number of categories:', Object.keys(findings.categoryFindings).length);
    }
  }, [assessment, findings]);
  const { t } = useTranslation();
  
  // Define maturity level colors to match the requested color scheme from image 1
  const maturityLevelColors = {
    'None': '#333333',       // Dark Gray/Black (0%)
    'Initial': '#990000',    // Dark Red (1-19%)
    'Defined': '#E67E22',    // Orange (20-49%)
    'Managed': '#F1C40F',    // Yellow/Gold (50-74%)
    'Compliant': '#2980B9',  // Blue (75-90%)
    'Optimized': '#27AE60'   // Green (91-100%)
  };

  // Function to get color based on score
  const getColorForScore = (score) => {
    if (score < 1) return maturityLevelColors['None'];
    if (score < 20) return maturityLevelColors['Initial'];
    if (score < 50) return maturityLevelColors['Defined'];
    if (score < 75) return maturityLevelColors['Managed'];
    if (score < 91) return maturityLevelColors['Compliant'];
    return maturityLevelColors['Optimized'];
  };

  // Function to get maturity level based on score
  const getMaturityLevelForScore = (score) => {
    if (score < 1) return 'None';
    if (score < 20) return 'Initial';
    if (score < 50) return 'Defined';
    if (score < 75) return 'Managed';
    if (score < 91) return 'Compliant';
    return 'Optimized';
  };
  
  // Function to extract category scores from findings data
  const getCategoryScores = (findings) => {
    // Check if findings has categoryFindings property
    if (findings && findings.categoryFindings) {
      return Object.fromEntries(
        Object.entries(findings.categoryFindings)
          .filter(([, data]) => data.score > 0) // Only include categories with scores > 0
          .map(([category, data]) => [
            category, data.score || 0
          ])
      );
    }
    
    // Check if findings has categories property
    if (findings && findings.categories) {
      return Object.fromEntries(
        Object.entries(findings.categories)
          .filter(([, data]) => data.score > 0) // Only include categories with scores > 0
          .map(([category, data]) => [
            category, data.score || 0
          ])
      );
    }
    
    // If neither structure exists, check if assessment has categoryScores
    if (assessment && assessment.categoryScores) {
      // Filter out categories with 0% scores
      return Object.fromEntries(
        Object.entries(assessment.categoryScores)
          .filter(([, score]) => score > 0)
      );
    }
    
    // Return empty object if no scores found
    return {};
  };
  
  // Function to get category data for detailed findings
  const getCategoryData = (findings) => {
    // If findings has categoryFindings property
    if (findings && findings.categoryFindings && Object.keys(findings.categoryFindings).length > 0) {
      return Object.entries(findings.categoryFindings)
        .filter(([, data]) => data.score > 0); // Only include categories with scores > 0
    }
    
    // If findings has categories property
    if (findings && findings.categories && Object.keys(findings.categories).length > 0) {
      return Object.entries(findings.categories)
        .filter(([, data]) => data.score > 0); // Only include categories with scores > 0
    }
    
    // If neither exists but we have assessment data with category scores
    if (assessment && assessment.categoryScores) {
      // Create a basic structure for each category
      return Object.entries(assessment.categoryScores)
        .filter(([, score]) => score > 0) // Only include categories with scores > 0
        .map(([category, score]) => {
          return [category, {
            score: score,
            maturityLevel: getMaturityLevelForScore(score),
            findings: `No detailed findings available for ${category}.`,
            recommendations: `No detailed recommendations available for ${category}.`
          }];
        });
    }
    
    // Return empty array if no category data found
    return [];
  };

  return (
    <div className="findings-recommendations-container">
      {/* Report Header with Logo */}
      <div style={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center',
        marginBottom: '20px',
        borderBottom: '2px solid #D2BA7B',
        paddingBottom: '15px',
        backgroundColor: '#FFFAFA'
      }}>
        <div>
          <h2 style={{ color: '#243444', margin: 0, fontWeight: 'bold' }}>{t('reports.findingsRecommendations')}</h2>
          <p style={{ color: '#4C5454', margin: '5px 0 0 0' }}>
            {assessment?.title || t('reports.maturityAssessment')}
          </p>
        </div>
        <div style={{ textAlign: 'right' }}>
          <img 
            src="/images/Navigator Logo Transparent.png" 
            alt="Business Resilience Navigator Logo" 
            style={{ maxWidth: '180px', height: 'auto' }} 
          />
        </div>
      </div>

      {/* Assessment Information */}
      {assessment && (
        <div className="mb-4">
          <div 
            style={{ 
              backgroundColor: '#FFFAFA', 
              borderRadius: '8px', 
              padding: '20px',
              boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
            }}
          >
            <div className="row">
              <div className="col-md-6">
                <p><strong>{t('reports.organization')}:</strong> {assessment.organizationName || 'Your Organization'}</p>
                <p><strong>{t('reports.industry')}:</strong> {assessment.industryType || 'Not Specified'}</p>
                <p><strong>{t('reports.completedOn')}:</strong> {new Date(assessment.updatedAt).toLocaleDateString()}</p>
              </div>
              <div className="col-md-6">
                <div className="d-flex align-items-center justify-content-end">
                  <div 
                    style={{ 
                      width: '80px', 
                      height: '80px', 
                      borderRadius: '50%', 
                      backgroundColor: getColorForScore(assessment.score || 0),
                      color: '#FFFAFA',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontWeight: 'bold',
                      fontSize: '24px',
                      marginRight: '15px'
                    }}
                  >
                    {Math.round(assessment.score) || 0}%
                  </div>
                  <div>
                    <p className="mb-0"><strong>{t('reports.overallMaturity')}:</strong></p>
                    <p className="mb-0" style={{ 
                      fontSize: '18px', 
                      fontWeight: 'bold',
                      color: getColorForScore(assessment.score || 0)
                    }}>
                      {getMaturityLevelForScore(assessment.score || 0)}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Category Scores */}
      <div className="mb-4">
        <h3 style={{ color: '#243444', borderBottom: '2px solid #D2BA7B', paddingBottom: '10px' }}>
          {t('reports.categoryScores')}
        </h3>
        
        {findings && (
          <CategoryScoresTable 
            assessment={assessment} 
            categoryScores={getCategoryScores(findings)}
          />
        )}
      </div>
      
      {/* Maturity Levels */}
      <div className="mb-4">
        <div style={{ 
          backgroundColor: '#f9f9f9', 
          borderRadius: '8px', 
          padding: '20px',
          marginBottom: '20px'
        }}>
          <h4 style={{ color: '#243444', marginBottom: '15px' }}>{t('reports.maturityLevels')}</h4>
          <div className="row">
            <div className="col-md-6">
              <div className="maturity-level-none" style={{ 
                padding: '10px 15px',
                borderRadius: '5px',
                marginBottom: '10px'
              }}>
                <strong>None (0%)</strong>: {t('reports.noneDescription')}
              </div>
              <div className="maturity-level-initial" style={{ 
                padding: '10px 15px',
                borderRadius: '5px',
                marginBottom: '10px'
              }}>
                <strong>Initial (1-19%)</strong>: {t('reports.initialDescription')}
              </div>
              <div className="maturity-level-defined" style={{ 
                padding: '10px 15px',
                borderRadius: '5px',
                marginBottom: '10px'
              }}>
                <strong>Defined (20-49%)</strong>: {t('reports.definedDescription')}
              </div>
            </div>
            <div className="col-md-6">
              <div className="maturity-level-managed" style={{ 
                padding: '10px 15px',
                borderRadius: '5px',
                marginBottom: '10px'
              }}>
                <strong>Managed (50-74%)</strong>: {t('reports.managedDescription')}
              </div>
              <div className="maturity-level-compliant" style={{ 
                padding: '10px 15px',
                borderRadius: '5px',
                marginBottom: '10px'
              }}>
                <strong>Compliant (75-90%)</strong>: {t('reports.compliantDescription')}
              </div>
              <div className="maturity-level-optimized" style={{ 
                padding: '10px 15px',
                borderRadius: '5px',
                marginBottom: '10px'
              }}>
                <strong>Optimized (91-100%)</strong>: {t('reports.optimizedDescription')}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Executive Summary */}
      {findings.executiveSummary && (
        <div className="mb-4">
          <h3 style={{ color: '#243444', borderBottom: '2px solid #D2BA7B', paddingBottom: '10px' }}>
            {t('reports.executiveSummary')}
          </h3>
          <div dangerouslySetInnerHTML={{ __html: findings.executiveSummary }}></div>
        </div>
      )}

      {/* Key Strengths */}
      {findings.keyStrengths && (
        <div className="mb-4">
          <h3 style={{ color: '#243444', borderBottom: '2px solid #D2BA7B', paddingBottom: '10px' }}>
            {t('reports.keyStrengths')}
          </h3>
          <div dangerouslySetInnerHTML={{ __html: findings.keyStrengths }}></div>
        </div>
      )}

      {/* Areas for Improvement */}
      {findings.areasForImprovement && (
        <div className="mb-4">
          <h3 style={{ color: '#243444', borderBottom: '2px solid #D2BA7B', paddingBottom: '10px' }}>
            {t('reports.areasForImprovement')}
          </h3>
          <div dangerouslySetInnerHTML={{ __html: findings.areasForImprovement }}></div>
        </div>
      )}

      {/* Detailed Category Findings */}
      <div className="mb-4">
        <h3 style={{ color: '#243444', borderBottom: '2px solid #D2BA7B', paddingBottom: '10px' }}>
          {t('reports.detailedCategoryFindings')}
        </h3>
        
        {getCategoryData(findings).length > 0 ? (
          getCategoryData(findings).map(([category, data]) => (
            <div key={category} className="card mb-4">
              <div className={`card-header maturity-level-${getMaturityLevelForScore(data.score).toLowerCase()}`} style={{ color: '#FFFAFA' }}>
                <div className="d-flex justify-content-between align-items-center">
                  <div>
                    <h5 className="mb-0">{category}</h5>
                    <div style={{ 
                      display: 'inline-block',
                      backgroundColor: '#7C1227',
                      color: 'white',
                      padding: '2px 8px',
                      borderRadius: '4px',
                      fontSize: '12px',
                      marginTop: '5px'
                    }}>
                      Maturity Level: {data.score ? `${getMaturityLevelForScore(data.score)} (${Math.round(data.score)}%)` : 'None (0%)'}
                    </div>
                  </div>
                  <div 
                    style={{ 
                      backgroundColor: '#FFFAFA', 
                      color: getColorForScore(data.score),
                      borderRadius: '50%',
                      width: '40px',
                      height: '40px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontWeight: 'bold'
                    }}
                  >
                    {Math.round(data.score) || 0}%
                  </div>
                </div>
              </div>
              <div className="card-body">
                <div className="mb-2">
                  <p className="mb-0"><strong>{t('reports.maturityLevel')}:</strong> {getMaturityLevelForScore(data.score)} ({Math.round(data.score)}%)</p>
                </div>
                
                {/* Findings */}
                <div className="mb-3">
                  <h5 style={{ color: '#243444' }}>{t('reports.findings')}</h5>
                  <div dangerouslySetInnerHTML={{ __html: data.findings }}></div>
                </div>
                
                {/* Recommendations */}
                <div className="mb-3">
                  <h5 style={{ color: '#243444' }}>{t('reports.recommendations')}</h5>
                  <div dangerouslySetInnerHTML={{ __html: data.recommendations }}></div>
                </div>
                
                {/* Specific Question Recommendations */}
                {data.questionRecommendations && data.questionRecommendations.length > 0 && (
                  <div>
                    <h5 style={{ color: '#243444' }}>{t('reports.specificRecommendations')}</h5>
                    <div className="accordion" id={`accordion-${category.replace(/\s+/g, '-')}`}>
                      {data.questionRecommendations.map((item, index) => (
                        <div className="accordion-item border-0 mb-2" key={index}>
                          <h2 className="accordion-header" id={`heading-${category}-${index}`}>
                            <button 
                              className="accordion-button collapsed" 
                              type="button" 
                              data-bs-toggle="collapse" 
                              data-bs-target={`#collapse-${category}-${index}`} 
                              aria-expanded="false" 
                              aria-controls={`collapse-${category}-${index}`}
                              style={{ backgroundColor: '#f8f8f8', color: '#243444' }}
                            >
                              <div>
                                <div className="d-flex align-items-center">
                                  <div 
                                    style={{ 
                                      width: '30px', 
                                      height: '30px', 
                                      borderRadius: '50%', 
                                      backgroundColor: getColorForScore(item.score),
                                      color: '#FFFAFA',
                                      display: 'flex',
                                      alignItems: 'center',
                                      justifyContent: 'center',
                                      fontWeight: 'bold',
                                      fontSize: '14px',
                                      marginRight: '10px'
                                    }}
                                  >
                                    {item.score}%
                                  </div>
                                  <span>{item.question}</span>
                                </div>
                              </div>
                            </button>
                          </h2>
                          <div 
                            id={`collapse-${category}-${index}`} 
                            className="accordion-collapse collapse" 
                            aria-labelledby={`heading-${category}-${index}`} 
                            data-bs-parent={`#accordion-${category.replace(/\s+/g, '-')}`}
                          >
                            <div className="accordion-body">
                              <div dangerouslySetInnerHTML={{ __html: item.recommendation }}></div>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
          ))
        ) : (
          <p>{t('reports.noDetailedFindingsAvailable')}</p>
        )}
      </div>
      
      {/* Footer with copyright - This will be visible in the PDF */}
      <div style={{ 
        marginTop: '50px', 
        borderTop: '2px solid #D2BA7B',
        paddingTop: '20px',
        paddingBottom: '20px',
        textAlign: 'center',
        backgroundColor: '#243444',
        color: '#FFFAFA',
        fontSize: '14px',
        borderRadius: '0 0 8px 8px'
      }}>
        <p style={{ margin: 0 }}>&copy; {new Date().getFullYear()} Business Resilience Navigator. All rights reserved.</p>
      </div>
    </div>
  );
};

export default FindingsRecommendations;
